import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import { LinkContainer } from 'react-router-bootstrap';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useAuth } from '../util/auth';
import { useLocation } from '../util/router';
import { getActiveItems } from '../util/config';
import './NavbarCustom.scss';
import { checkHasPermission, isAdmin } from '../util/util';
import { RULES, SCREEN } from '../util/Constant';
import { useGetPersonnel } from '../util/db';
import { getInsightSQCAppUrl } from '../util/cross-app-urls';

const ShowLink = ({ to, children, show }) => {
  if (!show) return null;

  return (
    <LinkContainer to={to}>
      <NavDropdown.Item active={false}>{children}</NavDropdown.Item>
    </LinkContainer>
  );
};

ShowLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  show: PropTypes.bool.isRequired
};

const ShowExtLink = ({ to, children, show = true }) => {
  if (!show) return null;

  // Determine if this is a truly external URL (different domain)
  const isExternalUrl = to && (to.startsWith('http://') || to.startsWith('https://'));

  return (
    <NavDropdown.Item
      active={false}
      href={to}
      target={isExternalUrl ? '_blank' : undefined}
      rel={isExternalUrl ? 'noopener noreferrer' : undefined}
    >
      {children}
    </NavDropdown.Item>
  );
};

ShowExtLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  show: PropTypes.bool
};

function NavbarCustom({
  bg = 'light',
  variant = 'light',
  expand = 'md',
  logo = 'https://via.placeholder.com/150',
  logoAncillary = 'https://via.placeholder.com/150'
}) {
  const pathsWithMainLogo = [
    '',
    '/',
    '/chart',
    '/master',
    '/notifications',
    '/settings',
    '/settings/general',
    '/settings/password',
    '/pipeline',
    '/samples',
    '/auth/signin',
    '/print-calculation',
    '/print-initial-assessment',
    '/print-periodic-assessment',
    '/print-report',
    '/1090-w-ARV'
  ];

  const auth = useAuth();
  const activeItems = getActiveItems(window.location.hostname, window.location.href);
  const location = useLocation();

  const [logoToUse, setLogoToUse] = useState(
    pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary
  );

  const { data: personnel } = useGetPersonnel();

  const [permissionData, setPermissionData] = useState([]);
  const [insightSQCAppUrl, setInsightSQCAppUrl] = useState(null);

  // Get InsightSQC app URL for cross-linking
  useEffect(() => {
    const url = getInsightSQCAppUrl();
    setInsightSQCAppUrl(url);
  }, []);

  useEffect(() => {
    setLogoToUse(pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary);
  }, [location?.pathname]);

  useEffect(() => {
    setLogoToUse(pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary);
  }, [location.pathname]);

  useEffect(() => {
    if (!personnel || !auth?.user?.email || !auth?.permissionData) return;

    const matchingUser = personnel.find((person) => person.email === auth.user.email);

    if (!matchingUser) return;
    const userRole = matchingUser.permission;
    const rolePermissionData = auth.permissionData[userRole];
    setPermissionData(rolePermissionData || null);
  }, [personnel, auth]);

  return (
    <Navbar bg={bg} variant={variant} expand={expand}>
      <Container className="navbar-brand-left-aligned">
        <LinkContainer to="/">
          <Navbar.Brand>
            <img className="d-inline-block align-top" src={logoToUse} alt="Logo" height="55" />
          </Navbar.Brand>
        </LinkContainer>
        <Navbar.Toggle aria-controls="navbar-nav" className="border-0" />
        <Navbar.Collapse id="navbar-nav" className="justify-content-end no-print">
          <Nav>
            {auth.user && (
              <NavDropdown id="dropdown" title="Account">
                {/* SPC Dashboard - Fuel local link */}
                <ShowLink to="/spc-dashboard" show={activeItems.spcDashboard}>
                  SPC Dashboard
                </ShowLink>

                {/* SPC Data and Charts - Fuel local link */}
                <ShowLink to="/spc" show={activeItems.spcDataCharts}>
                  SPC Data and Charts
                </ShowLink>

                {/* SPC Master Data - Fuel local link */}
                <ShowLink to="/spcmd" show={activeItems.spcMasterData}>
                  SPC Master Data
                </ShowLink>

                {/* Fuel menu items - local links */}
                <ShowLink to="/ilcp" show={activeItems.iLCP}>
                  ILCP
                </ShowLink>

                {/* 1090 accuracy with ARV - Fuel local link */}
                <ShowLink to="/1090-w-ARV" show={activeItems.arv}>
                  1090 accuracy with ARV
                </ShowLink>

                <ShowLink to="/instrument/events" show={activeItems.instrument}>
                  Instrument Management
                </ShowLink>
                <ShowLink to="/product" show={activeItems.productComp}>
                  Product Comp
                </ShowLink>

                {/* Pipeline - Fuel local link */}
                <ShowLink to="/pipeline" show={activeItems.pipeline}>
                  Pipeline
                </ShowLink>

                <ShowExtLink to="/fuelcomp" show={activeItems.fuelComp}>
                  Fuel Comp
                </ShowExtLink>
                <ShowLink to="/samples" show={activeItems.samples}>
                  Samples
                </ShowLink>

                <NavDropdown.Divider />
                <ShowLink to="/settings/general" show={true}>
                  Settings
                </ShowLink>
                <NavDropdown.Divider />
                <ShowExtLink to={insightSQCAppUrl || '#'} show={!!insightSQCAppUrl}>
                  Go to Insight SQC
                </ShowExtLink>
                <NavDropdown.Divider />
                <LinkContainer to="/auth/signout">
                  <NavDropdown.Item
                    active={false}
                    onClick={(e) => {
                      e.preventDefault();
                      auth.signout();
                    }}
                  >
                    Sign out
                  </NavDropdown.Item>
                </LinkContainer>
              </NavDropdown>
            )}

            {!auth.user && (
              <Nav.Item>
                <LinkContainer to="/auth/signin">
                  <Nav.Link active={false}>Sign in</Nav.Link>
                </LinkContainer>
              </Nav.Item>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

NavbarCustom.propTypes = {
  bg: PropTypes.string,
  variant: PropTypes.string,
  expand: PropTypes.string,
  logo: PropTypes.string,
  logoAncillary: PropTypes.string
};

export default NavbarCustom;
